import { Form, Button, Upload, Table, Alert, Row, Col, Card, Divider } from "antd";
import { useState } from "react";
import { UploadOutlined, DownloadOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import { Download, UploadCloud, CheckCircle2 } from "lucide-react";
import BrandSelect from "../../components/select/BrandSelect";
import CategorySelect from "../../components/select/CategorySelect";
import SegmentSelect from "../../components/select/SegmentSelect";

const ImportProduct = () => {

  const api = {
    import    :   apiPath.importProduct
  }
    
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [errorLogs, setErrorLogs] = useState([]);
  const [brandId, setBrandId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [segmentId, setSegmentId] = useState(null);

  const downloadSample = () => {
    const link      =   document.createElement("a");
    link.href       =   "/product_sample.csv";
    link.download   =   "product_sample.csv";
    link.click();
  };

  const handleUpload = {
    accept: ".csv,.xlsx,.xls",
    beforeUpload: (file) => {
      const isValid =
        file.type === "text/csv" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".csv") ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls");
      if (!isValid) {
        ShowToast("Please upload a CSV or Excel (.xlsx, .xls) file", Severty.ERROR);
        return false;
      }
      if (file.size / 1024 / 1024 > 15) {
        ShowToast("File size must be less than 15MB", Severty.ERROR);
        return false;
      }
      setFileList([file]);
      return false;
    },
  };

  const handleImport = () => {
    
    if (!fileList.length) {
      setErrorLogs(["Please select a file first"]);
      return;
    }

    const formData = new FormData();
    formData.append("file", fileList[0]);

    if (brandId) formData.append("brand_id", brandId);
    if (categoryId) formData.append("category_id", categoryId);
    if (segmentId) formData.append("segment_id", segmentId);

    setLoading(true);
    setErrorLogs([]);

    request({
      url: api.import,
      method: "POST",
      data: formData,
      onSuccess: (data) => {
        setLoading(false);
        setImportResults(data.data);
        
        const hasFailed = data.data?.failed > 0;
        
        if (hasFailed && data.data?.errors?.length > 0) {
          const detailedErrors = data.data.errors.map(error => 
            `Row ${error.row} (${error.data?.part_number}): ${error.errors?.join(", ") || "Unknown error"}`
          );

          setErrorLogs(detailedErrors);
          ShowToast("Import completed with errors", Severty.ERROR);

        } else {

          ShowToast(data.message || "Import completed successfully", Severty.SUCCESS);
          setTimeout(() => {
            navigate("/product" + location.search);
          }, 1000);
        }
      },
      onError: (error) => {
        setLoading(false);
        const errorMsg = error.response?.data?.message || "Import failed";
        setErrorLogs([errorMsg]);
        setImportResults(error.response?.data);
      },
    });
  };

  const handleClose = () => {
    navigate("/product" + location.search);
  };

  const resultsColumns = [
    {
      title: "Part Number",
      dataIndex: "part_number",
      key: "part_number",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span style={{ color: status === "success" ? "green" : "red" }}>
          {status}
        </span>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
    },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24} sm={24} md={24} lg={24}>
          <Card
            variant={false}
            className="criclebox tablespace mb-6 shadow-sm"
            title="Import Products"
            extra={
              <Button
                type="primary"
                onClick={handleClose}
                icon={<ArrowLeftOutlined />}
                className="flex items-center gap-1"
              >
                Back
              </Button>
            }
          >
            <Form form={form} layout="vertical">
              
              {/* CSV Format Instructions */}
              <Card className="mb-6 border-l-4 border-l-blue-500">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 m-0">File Format Instructions</h3>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  Please follow the guidelines below before uploading your file:
                </p>

                <Row gutter={[24, 16]}>
                  <Col xs={24} md={12}>
                    <div className="space-y-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">File Format:</span>
                          <p className="text-gray-600 text-sm mt-1">Must be <strong>.csv</strong>, <strong>.xlsx</strong> or <strong>.xls</strong> format</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Column Headers:</span>
                          <p className="text-gray-600 text-sm mt-1">First row must contain headers</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Required Columns:</span>
                          <p className="text-gray-600 font-mono bg-gray-100 p-2 rounded mt-1">
                            part_number, price, en_name
                          </p>
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div className="space-y-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Data Quality:</span>
                          <p className="text-gray-600 text-sm mt-1">No empty rows or special characters (%, #, &, *) in part numbers</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">Price Format:</span>
                          <p className="text-gray-600 text-sm mt-1">Numeric only, no currency symbols (₹, $, etc.)</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">File Size:</span>
                          <p className="text-gray-600 text-sm mt-1">Maximum <strong>15 MB</strong></p>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Divider className="my-4" />

                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 m-0">
                    Download a sample CSV file to ensure correct formatting:
                  </p>
                  <Button
                    type="dashed"
                    onClick={downloadSample}
                    icon={<Download className="w-4 h-4" />}
                    className="flex items-center gap-2"
                  >
                    Download Sample
                  </Button>
                </div>
              </Card>

              {/* Dropdowns Section */}
              <Card className="border-l-4 border-l-blue-500 p-4 rounded-lg">
                <h3 className="text-md font-semibold text-gray-800 mb-4">Select below fields</h3>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label="Select Brand"
                      className="font-medium"
                      required
                      rules={[{ required: true, message: 'Please select a brand' }]}
                    >
                      <BrandSelect 
                        value={brandId} 
                        onChange={setBrandId}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label="Select Category"
                      className="font-medium"
                      required
                      rules={[{ required: true, message: 'Please select a category' }]}
                    >
                      <CategorySelect
                        brandId={brandId}
                        disabled={!brandId}
                        shouldFetch={!!brandId}
                        value={categoryId}
                        onChange={setCategoryId}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label="Select Segment"
                      className="font-medium"
                      required
                      rules={[{ required: true, message: 'Please select a segment' }]}
                    >
                      <SegmentSelect
                        brandId={brandId}
                        disabled={!brandId}
                        shouldFetch={!!brandId}
                        value={segmentId}
                        onChange={setSegmentId}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Upload CSV / Excel */}
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label="Select CSV / Excel File"
                      required
                      className="font-medium"
                    >
                      <Upload {...handleUpload} maxCount={1}>
                        <Button className="flex items-center gap-2 w-full justify-center">
                          <UploadCloud className="w-4 h-4" />
                          Click to Upload
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Import Results */}
              {importResults && (
                <>
                  <Alert
                    message={`Import Results: ${importResults.successful || 0} successful, ${importResults.failed || 0} failed`}
                    type={importResults.failed > 0 ? "warning" : "success"}
                    showIcon
                    className="mb-4"
                  />

                  {importResults.details?.length > 0 && (
                    <div className="mt-4 max-h-[300px] overflow-auto border rounded-md shadow-sm">
                      <Table
                        columns={resultsColumns}
                        dataSource={importResults.details}
                        rowKey={(_, idx) => idx}
                        pagination={false}
                        size="small"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Error Logs */}
              {errorLogs.length > 0 && (
                <Alert
                  message="Error"
                  description={
                    <ul className="m-0 pl-6 list-disc">
                      {errorLogs.map((log, idx) => (
                        <li key={idx}>{log}</li>
                      ))}
                    </ul>
                  }
                  type="error"
                  showIcon
                  className="mb-4"
                />
              )}

              {/* Footer Buttons */}
              <Row className="mt-6">
                <Col xs={24}>
                  <div className="flex justify-end gap-3">
                    <Button onClick={handleClose}>Cancel</Button>

                    <Button
                      type="primary"
                      loading={loading}
                      onClick={handleImport}
                      disabled={!fileList.length}
                    >
                      Import
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>

  );
};

export default ImportProduct;
